import { Fragment, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SignOut, UserCircle } from "@phosphor-icons/react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  DropdownMenuItem,
  DropdownMenuSeparator,
  SidebarCollapseButton,
  TopBar,
  UserMenu,
  UserMenuContent,
  UserMenuHeader,
  UserMenuTrigger,
} from "@hulahoop/ui";
import { ROUTES } from "@/app/routes";
import { useAuth } from "@/contexts/AuthContext";
import { CopyPageLink } from "./CopyPageLink";
import { ShellRecordCrumb } from "./ShellRecordCrumb";

interface ShellTopBarProps {
  collection: string;
  collectionHref?: string;
  record?: string;
  crumbs?: { label: string; href: string }[];
  recordMenu?: ReactNode;
  recordExtra?: ReactNode;
  actions?: ReactNode;
  onSignOut: () => void;
}

const initialsOf = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export function ShellTopBar({
  collection,
  collectionHref,
  record,
  crumbs,
  recordMenu,
  recordExtra,
  actions,
  onSignOut,
}: ShellTopBarProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  return (
    <TopBar surface="chrome" style={{ "--indicator-ring": "var(--sidebar)" } as CSSProperties}>
      <span className="flex min-w-0 items-center gap-2">
        <SidebarCollapseButton expand aria-label={t("nav.expandSidebar")} />
        <Breadcrumb>
          <BreadcrumbList>
            {record && crumbs ? (
              <>
                {crumbs.map((crumb) => (
                  <Fragment key={crumb.href}>
                    <BreadcrumbItem>
                      <BreadcrumbLink render={<Link to={crumb.href} />}>
                        {crumb.label}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </Fragment>
                ))}
                <ShellRecordCrumb record={record} menu={recordMenu} />
              </>
            ) : record ? (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink render={<Link to={collectionHref ?? "."} />}>
                    {collection}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <ShellRecordCrumb record={record} menu={recordMenu} />
              </>
            ) : (
              <BreadcrumbItem>
                <BreadcrumbPage>{collection}</BreadcrumbPage>
              </BreadcrumbItem>
            )}
          </BreadcrumbList>
        </Breadcrumb>
        {recordExtra}
      </span>
      <div className="flex items-center gap-2">
        {actions}
        <CopyPageLink />
        <UserMenu>
          <UserMenuTrigger
            className="ml-1.5"
            name={auth.user?.name ?? ""}
            avatarSrc={auth.user?.profile_picture_signed_url}
            avatarFallback={initialsOf(auth.user?.name ?? "")}
            aria-label={t("nav.userMenu")}
          />
          <UserMenuContent>
            <UserMenuHeader name={auth.user?.name ?? ""} email={auth.user?.email ?? ""} />
            <DropdownMenuSeparator />
            <DropdownMenuItem className="font-medium" render={<Link to={ROUTES.PROFILE} />}>
              <UserCircle /> {t("shell.profile")}
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" className="font-medium" onClick={onSignOut}>
              <SignOut /> {t("nav.logout")}
            </DropdownMenuItem>
          </UserMenuContent>
        </UserMenu>
      </div>
    </TopBar>
  );
}
